import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader2, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Read-only admin surface for the Sayısal V1 content-package plane.
// Consumes ONLY existing admin endpoints; no mutations (no register /
// activate / revoke / reactivate buttons). All lifecycle transitions
// remain reviewer-gated at the API layer and are performed
// out-of-band until the pilot content is ready.

const TIER_LABELS = {
  LGS_SAY: "LGS Sayısal",
  TYT_SAY: "TYT Sayısal",
  AYT_SAY: "AYT Sayısal",
};

const STATUS_STYLES = {
  registered: "bg-zinc-100 text-zinc-700",
  activated: "bg-emerald-100 text-emerald-800",
  revoked: "bg-rose-100 text-rose-800",
  superseded: "bg-amber-100 text-amber-800",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-zinc-100 text-zinc-700";
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}
      data-testid={`pkg-status-${status || "unknown"}`}
    >
      {status || "—"}
    </span>
  );
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("tr-TR");
  } catch {
    return iso;
  }
}

export default function AdminSayisalPackages() {
  const [packages, setPackages] = useState(null);
  const [health, setHealth] = useState(null);
  const [integrity, setIntegrity] = useState(null);
  const [events, setEvents] = useState(null);
  const [tierFilter, setTierFilter] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [expandedPkg, setExpandedPkg] = useState(null);
  const [auditByPkg, setAuditByPkg] = useState({});

  async function loadAll() {
    setRefreshing(true);
    try {
      const query = tierFilter ? `?tier=${encodeURIComponent(tierFilter)}` : "";
      const [pkgs, hlth, intg, ops] = await Promise.all([
        api.get(`/sayisal/admin/packages${query}`).then((r) => r.data).catch((e) => ({ __err: formatApiError(e) })),
        api.get(`/sayisal/admin/health`).then((r) => r.data).catch((e) => ({ __err: formatApiError(e) })),
        api.get(`/sayisal/admin/integrity-scan`).then((r) => r.data).catch((e) => ({ __err: formatApiError(e) })),
        api.get(`/sayisal/admin/operational-events?limit=50`).then((r) => r.data).catch((e) => ({ __err: formatApiError(e) })),
      ]);
      setPackages(pkgs);
      setHealth(hlth);
      setIntegrity(intg);
      setEvents(ops);
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierFilter]);

  async function toggleAudit(pkgId) {
    if (expandedPkg === pkgId) {
      setExpandedPkg(null);
      return;
    }
    setExpandedPkg(pkgId);
    if (auditByPkg[pkgId]) return;
    try {
      const r = await api.get(`/sayisal/admin/packages/${pkgId}/audit?limit=100`);
      setAuditByPkg((m) => ({ ...m, [pkgId]: r.data?.events || [] }));
    } catch (e) {
      toast.error(formatApiError(e));
      setAuditByPkg((m) => ({ ...m, [pkgId]: [] }));
    }
  }

  const pkgList = packages?.packages || [];
  const pkgErr = packages?.__err;
  const healthErr = health?.__err;
  const integrityErr = integrity?.__err;
  const eventsList = events?.events || [];
  const eventsErr = events?.__err;
  const anomalies = integrity?.anomalies || [];

  return (
    <AppShell variant="admin">
      <div className="max-w-6xl mx-auto p-5 lg:p-10" data-testid="admin-sayisal-packages">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Sayısal · İçerik Motoru</div>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">
              Sayısal Paketler
            </h1>
            <p className="mt-2 text-zinc-600 max-w-2xl">
              Sınav Zekâsı (Exam Intelligence) paketlerinin yalnızca okuma modunda görünümü.
              Kayıt, aktivasyon, geri alma ve yeniden aktivasyon uzman inceleme akışında yapılır.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={tierFilter}
              onChange={(e) => setTierFilter(e.target.value)}
              className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900"
              data-testid="admin-pkg-tier-filter"
            >
              <option value="">Tüm tier'lar</option>
              <option value="LGS_SAY">LGS Sayısal</option>
              <option value="TYT_SAY">TYT Sayısal</option>
              <option value="AYT_SAY">AYT Sayısal</option>
            </select>
            <button
              type="button"
              onClick={loadAll}
              disabled={refreshing}
              className="h-9 px-3 rounded-md border border-zinc-300 bg-white text-sm text-zinc-900 hover:bg-zinc-50 disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="admin-pkg-refresh"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Yenile
            </button>
          </div>
        </div>

        {/* Health + Integrity summary */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="admin-pkg-health-card">
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Sağlık Özeti (24s)</div>
            {healthErr && <div className="mt-3 text-sm text-rose-700">{healthErr}</div>}
            {!healthErr && !health && <Loader2 className="w-4 h-4 animate-spin mt-3 text-zinc-400" />}
            {!healthErr && health && (
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {Object.entries(health)
                  .filter(([, v]) => typeof v !== "object" || v === null)
                  .slice(0, 12)
                  .map(([k, v]) => (
                    <div key={k} className="flex flex-col">
                      <dt className="text-[11px] uppercase tracking-wide text-zinc-500">{k}</dt>
                      <dd className="text-zinc-900 font-medium">{String(v ?? "—")}</dd>
                    </div>
                  ))}
              </dl>
            )}
          </div>
          <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="admin-pkg-integrity-card">
            <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Bütünlük Taraması</div>
            {integrityErr && <div className="mt-3 text-sm text-rose-700">{integrityErr}</div>}
            {!integrityErr && !integrity && <Loader2 className="w-4 h-4 animate-spin mt-3 text-zinc-400" />}
            {!integrityErr && integrity && (
              <div className="mt-3">
                <div className="text-2xl font-display font-bold text-zinc-900">
                  {anomalies.length}
                  <span className="ml-2 text-xs font-medium text-zinc-500 uppercase tracking-wide">anomali</span>
                </div>
                {anomalies.length > 0 ? (
                  <ul className="mt-3 space-y-1 text-xs text-zinc-700 max-h-32 overflow-y-auto">
                    {anomalies.slice(0, 20).map((a, i) => (
                      <li key={i} className="border-l-2 border-rose-300 pl-2">
                        <span className="font-medium">{a.kind || a.code || "anomaly"}</span>
                        {a.package_id && <span className="text-zinc-500"> · {a.package_id}</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="mt-2 text-xs text-emerald-700">Bütünlük ihlali bulunamadı.</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Packages table */}
        <div className="mt-8 rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="admin-pkg-list">
          <div className="px-5 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
            Paketler
          </div>
          {pkgErr && <div className="p-6 text-sm text-rose-700">{pkgErr}</div>}
          {!pkgErr && packages === null && (
            <div className="p-10 text-center text-zinc-500">
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            </div>
          )}
          {!pkgErr && packages && pkgList.length === 0 && (
            <div className="p-10 text-center text-zinc-500">
              Bu filtre için paket bulunamadı.
              {!tierFilter && (
                <div className="mt-1 text-xs">
                  Henüz hiçbir tier için aktif paket yok — pilot içeriği hazırlanıyor.
                </div>
              )}
            </div>
          )}
          {!pkgErr && pkgList.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 border-b border-zinc-200">
                  <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="px-4 py-3 font-semibold w-8"></th>
                    <th className="px-4 py-3 font-semibold">Tier</th>
                    <th className="px-4 py-3 font-semibold">Paket ID</th>
                    <th className="px-4 py-3 font-semibold">Sürüm</th>
                    <th className="px-4 py-3 font-semibold">Durum</th>
                    <th className="px-4 py-3 font-semibold">Kapsam</th>
                    <th className="px-4 py-3 font-semibold">Oluşturma</th>
                  </tr>
                </thead>
                <tbody>
                  {pkgList.map((p) => {
                    const isOpen = expandedPkg === p.id;
                    const audit = auditByPkg[p.id];
                    return (
                      <>
                        <tr
                          key={p.id}
                          className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 cursor-pointer"
                          onClick={() => toggleAudit(p.id)}
                          data-testid={`admin-pkg-row-${p.id}`}
                        >
                          <td className="px-4 py-3 text-zinc-400">
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-medium text-zinc-900">{TIER_LABELS[p.tier] || p.tier}</span>
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-zinc-700">{p.id || p.package_id || "—"}</td>
                          <td className="px-4 py-3 text-zinc-700">{p.package_version || "—"}</td>
                          <td className="px-4 py-3"><StatusPill status={p.status} /></td>
                          <td className="px-4 py-3 text-xs text-zinc-600">
                            {Array.isArray(p.subject_scope) ? p.subject_scope.join(", ") : "—"}
                          </td>
                          <td className="px-4 py-3 text-xs text-zinc-500">{fmtTs(p.created_at)}</td>
                        </tr>
                        {isOpen && (
                          <tr key={`${p.id}-audit`} className="border-b border-zinc-100 bg-zinc-50/50">
                            <td colSpan={7} className="px-4 py-4">
                              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
                                Denetim Kayıtları
                              </div>
                              {audit === undefined && (
                                <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
                              )}
                              {audit && audit.length === 0 && (
                                <div className="text-xs text-zinc-500">Kayıt yok.</div>
                              )}
                              {audit && audit.length > 0 && (
                                <ul className="space-y-1 max-h-64 overflow-y-auto" data-testid={`admin-pkg-audit-${p.id}`}>
                                  {audit.map((ev, i) => (
                                    <li key={i} className="text-xs text-zinc-700 flex items-baseline gap-2">
                                      <span className="text-zinc-400 font-mono w-40 shrink-0">{fmtTs(ev.created_at || ev.at)}</span>
                                      <span className="font-medium text-zinc-900">{ev.event_type || ev.kind}</span>
                                      {ev.reviewer_id && <span className="text-zinc-500">· reviewer:{ev.reviewer_id}</span>}
                                      {ev.reason && <span className="text-zinc-500">· {ev.reason}</span>}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Operational events */}
        <div className="mt-8 rounded-2xl border border-zinc-200 bg-white overflow-hidden" data-testid="admin-pkg-events">
          <div className="px-5 py-3 border-b border-zinc-200 text-xs uppercase tracking-wider text-zinc-500 font-semibold">
            Operasyonel Olaylar (son 50)
          </div>
          {eventsErr && <div className="p-6 text-sm text-rose-700">{eventsErr}</div>}
          {!eventsErr && events === null && (
            <div className="p-10 text-center text-zinc-500">
              <Loader2 className="w-4 h-4 animate-spin mx-auto" />
            </div>
          )}
          {!eventsErr && events && eventsList.length === 0 && (
            <div className="p-8 text-center text-xs text-zinc-500">Kayıt yok.</div>
          )}
          {!eventsErr && eventsList.length > 0 && (
            <ul className="divide-y divide-zinc-100 max-h-96 overflow-y-auto">
              {eventsList.map((ev, i) => (
                <li key={i} className="px-5 py-2.5 text-xs flex items-baseline gap-2">
                  <span className="text-zinc-400 font-mono w-40 shrink-0">{fmtTs(ev.created_at || ev.at)}</span>
                  <span className="font-medium text-zinc-900">{ev.kind || ev.event_type || "event"}</span>
                  {ev.correlations && (
                    <span className="text-zinc-500 truncate">
                      {Object.entries(ev.correlations).slice(0, 3).map(([k, v]) => `${k}=${v}`).join(" · ")}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}
